import type { EconomyConfig } from '../config/types';
import type { EconomyFns } from './economy';
import type { MultiplierFns } from './multipliers';
import type { GameState } from './state';
import { isHeroHome } from './state';
import { managedRates, type SimContext } from './simulation';
import { now } from './clock';

export interface OfflineResult {
  gold: number;
  elapsedSeconds: number;
  cappedSeconds: number;
  capSeconds: number;
  managedThroughput: number;
  valueMult: number;
  rolledBack: boolean;
  adsApplied: boolean;
}

export interface OfflineOptions {
  /** Apply offline.adMultiplier (rewarded-ad double at collection). Default false. */
  applyAdMultiplier?: boolean;
  nowMs?: number;
}

function deepPocketsHours(state: GameState, e: EconomyConfig): number {
  const up = e.shrine.upgrades.find((u) => u.id === 'deepPockets');
  const level = state.shrine['deepPockets'] ?? 0;
  const per = typeof up?.perLevel === 'number' ? up.perLevel : 0;
  return level * per;
}

function purchasedOfflineHours(state: GameState, e: EconomyConfig): number {
  const sinks = e.gemSinks as
    | { extraOfflineHoursPerPurchase?: { hours?: number } }
    | undefined;
  const hoursPer = sinks?.extraOfflineHoursPerPurchase?.hours;
  if (typeof hoursPer !== 'number') {
    throw new Error(
      'economy.json gemSinks.extraOfflineHoursPerPurchase.hours required',
    );
  }
  return state.extraOfflineHoursPurchased * hoursPer;
}

/** Cap seconds from economy.json offline.baseCapHours + shrine + purchases. */
export function offlineCapSeconds(state: GameState, e: EconomyConfig): number {
  const base = e.offline.baseCapHours;
  if (typeof base !== 'number') {
    throw new Error('economy.json offline.baseCapHours required');
  }
  return (base + deepPocketsHours(state, e) + purchasedOfflineHours(state, e)) * 3600;
}

/**
 * Offline earnings: slowest-link managed throughput × min(elapsed, cap) × valueMult.
 * - Unmanaged stations contribute 0
 * - Home bonus only if hero home at lastSavedAt
 * - No session ad-boost in valueMult; optional collection-time adMultiplier
 * - Clock rollback → 0 gold, no state break
 */
export function computeOfflineEarnings(
  state: GameState,
  ctx: Pick<SimContext, 'economy' | 'multipliers'> & { nowMs?: () => number },
  options: OfflineOptions = {},
): OfflineResult {
  const e = ctx.economy.config;
  const nowMs = options.nowMs ?? (ctx.nowMs ?? now)();
  const elapsedMs = nowMs - state.lastSavedAt;
  if (elapsedMs < 0) {
    return {
      gold: 0,
      elapsedSeconds: elapsedMs / 1000,
      cappedSeconds: 0,
      capSeconds: offlineCapSeconds(state, e),
      managedThroughput: 0,
      valueMult: 0,
      rolledBack: true,
      adsApplied: false,
    };
  }
  const elapsedSeconds = elapsedMs / 1000;
  const capSeconds = offlineCapSeconds(state, e);
  const cappedSeconds = Math.min(elapsedSeconds, capSeconds);

  const rates = managedRates(state, ctx as SimContext, state.lastSavedAt);
  const managedThroughput = rates.throughput;

  const valueMult = ctx.multipliers.valueMultiplier({
    siteIndex: state.site.index,
    shrineRichVeins: state.shrine['richVeins'] ?? 0,
    heroHome: isHeroHome(state, state.lastSavedAt),
    goldenLantern: state.entitlements.goldenLantern,
    adBoostActive: false,
  });

  const efficiency = e.offline.efficiency;
  let gold = managedThroughput * cappedSeconds * efficiency * valueMult;
  const adsApplied = options.applyAdMultiplier === true;
  if (adsApplied) {
    gold *= e.offline.adMultiplier;
  }

  return {
    gold,
    elapsedSeconds,
    cappedSeconds,
    capSeconds,
    managedThroughput,
    valueMult,
    rolledBack: false,
    adsApplied,
  };
}

/** Apply offline gold to state and advance lastSavedAt (caller still saves). */
export function applyOfflineEarnings(
  state: GameState,
  result: OfflineResult,
  nowMs: number,
): void {
  if (result.gold > 0) {
    state.currencies.gold += result.gold;
    state.site.earnedThisSite += result.gold;
    state.stats.lifetimeGold += result.gold;
  }
  // Never move lastSavedAt backwards (clock-rollback exploit via save).
  if (!result.rolledBack) {
    state.lastSavedAt = Math.max(state.lastSavedAt, nowMs);
  }
}

export type { EconomyFns, MultiplierFns };
